import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../LoadingSpinner";
import {
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  UserIcon,
  DocumentArrowDownIcon,
  ShieldExclamationIcon,
} from "@heroicons/react/24/outline";

const AccountActions = ({ showMessage, user }) => {
  const { logout } = useAuth();
  const [isLoading, setIsLoading] = useState({
    export: false,
    deactivate: false,
    delete: false,
  });
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");

  const handleExportData = async (format) => {
    setIsLoading((prev) => ({ ...prev, export: true }));
    try {
      // Mock API call - replace with actual implementation
      const response = await fetch("/api/users/me/export", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: JSON.stringify({ format }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `dz-metrics-data-export-${Date.now()}.${format.toLowerCase()}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        showMessage(
          "success",
          `Data exported successfully as ${format.toUpperCase()}`,
        );
      } else {
        throw new Error("Export failed");
      }
    } catch (error) {
      showMessage("error", "Failed to export data. Please try again later.");
    } finally {
      setIsLoading((prev) => ({ ...prev, export: false }));
    }
  };

  const handleDeactivateAccount = async () => {
    setIsLoading((prev) => ({ ...prev, deactivate: true }));
    try {
      // Mock API call - replace with actual implementation
      const response = await fetch("/api/users/me/deactivate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      if (response.ok) {
        showMessage(
          "success",
          "Account deactivated successfully. You will be logged out shortly.",
        );
        setTimeout(() => {
          logout();
        }, 2000);
      } else {
        throw new Error("Deactivation failed");
      }
    } catch (error) {
      showMessage(
        "error",
        "Failed to deactivate account. Please contact support.",
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, deactivate: false }));
      setShowDeactivateModal(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmationText !== "DELETE ACCOUNT") {
      showMessage("error", 'Please type "DELETE ACCOUNT" to confirm');
      return;
    }

    setIsLoading((prev) => ({ ...prev, delete: true }));
    try {
      // Mock API call - replace with actual implementation
      const response = await fetch("/api/users/me/delete", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      if (response.ok) {
        showMessage(
          "success",
          "Account deleted successfully. You will be logged out.",
        );
        setTimeout(() => {
          logout();
        }, 2000);
      } else {
        throw new Error("Deletion failed");
      }
    } catch (error) {
      showMessage("error", "Failed to delete account. Please contact support.");
    } finally {
      setIsLoading((prev) => ({ ...prev, delete: false }));
      setShowDeleteModal(false);
      setConfirmationText("");
    }
  };

  const DeactivateModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-lg bg-white">
        <div className="mt-3">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
          </div>
          <div className="mt-3 text-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Deactivate Account
            </h3>
            <div className="mt-2 px-7 py-3">
              <p className="text-sm text-gray-500">
                Are you sure you want to deactivate your account? This will:
              </p>
              <ul className="mt-2 text-sm text-gray-500 text-left">
                <li>• Hide your profile from other users</li>
                <li>• Pause all campaigns and reports</li>
                <li>• Suspend access to social accounts</li>
                <li>• Keep your data for reactivation</li>
              </ul>
              <p className="mt-2 text-sm font-medium text-gray-700">
                You can reactivate your account by logging in again.
              </p>
            </div>
          </div>
        </div>
        <div className="items-center px-4 py-3">
          <div className="flex space-x-3">
            <button
              onClick={() => setShowDeactivateModal(false)}
              className="flex-1 px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md shadow-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
              disabled={isLoading.deactivate}
            >
              Cancel
            </button>
            <button
              onClick={handleDeactivateAccount}
              disabled={isLoading.deactivate}
              className="flex-1 px-4 py-2 bg-yellow-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {isLoading.deactivate ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Deactivating...</span>
                </>
              ) : (
                <span>Deactivate Account</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const DeleteModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-lg bg-white">
        <div className="mt-3">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <TrashIcon className="h-6 w-6 text-red-600" />
          </div>
          <div className="mt-3 text-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Delete Account Permanently
            </h3>
            <div className="mt-2 px-7 py-3">
              <p className="text-sm text-red-600 font-medium">
                ⚠️ This action cannot be undone!
              </p>
              <p className="mt-2 text-sm text-gray-500">
                Deleting your account will permanently remove:
              </p>
              <ul className="mt-2 text-sm text-gray-500 text-left">
                <li>• All your profile data</li>
                <li>• Campaign history and reports</li>
                <li>• Social account connections</li>
                <li>• All analytics data</li>
              </ul>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type "DELETE ACCOUNT" to confirm:
                </label>
                <input
                  type="text"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="DELETE ACCOUNT"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="items-center px-4 py-3">
          <div className="flex space-x-3">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setConfirmationText("");
              }}
              className="flex-1 px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md shadow-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
              disabled={isLoading.delete}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteAccount}
              disabled={
                isLoading.delete || confirmationText !== "DELETE ACCOUNT"
              }
              className="flex-1 px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {isLoading.delete ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Deleting...</span>
                </>
              ) : (
                <span>Delete Forever</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Data Export */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Data Export</h3>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <DocumentArrowDownIcon className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800 font-medium">
                Export Your Data
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Download a copy of your account data, including profile
                information, campaigns, and analytics.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => handleExportData("JSON")}
            disabled={isLoading.export}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
          >
            {isLoading.export ? (
              <LoadingSpinner size="sm" />
            ) : (
              <ArrowDownTrayIcon className="h-4 w-4" />
            )}
            <span>Export as JSON</span>
          </button>

          <button
            onClick={() => handleExportData("CSV")}
            disabled={isLoading.export}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
          >
            {isLoading.export ? (
              <LoadingSpinner size="sm" />
            ) : (
              <ArrowDownTrayIcon className="h-4 w-4" />
            )}
            <span>Export as CSV</span>
          </button>
        </div>
      </div>

      {/* Transfer Ownership */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Account Ownership
        </h3>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-start">
            <UserIcon className="h-5 w-5 text-gray-600 mr-2 mt-0.5" />
            <div>
              <p className="text-sm text-gray-800 font-medium">
                Transfer Account Ownership
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Transfer ownership of your campaigns and data to another team
                member. This feature is coming soon.
              </p>
              <button
                disabled
                className="mt-3 px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed"
              >
                Coming Soon
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div>
        <h3 className="text-lg font-medium text-red-900 mb-4">Danger Zone</h3>
        <div className="border border-red-200 rounded-lg divide-y divide-red-200">
          {/* Deactivate Account */}
          <div className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                <ShieldExclamationIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900">
                    Deactivate Account
                  </h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Temporarily disable your account. You can reactivate it by
                    logging in again.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowDeactivateModal(true)}
                className="px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-lg hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-colors"
              >
                Deactivate
              </button>
            </div>
          </div>

          {/* Delete Account */}
          <div className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                <TrashIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900">
                    Delete Account
                  </h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Permanently delete your account and all associated data.
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Important Notes */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mr-2 mt-0.5" />
          <div>
            <p className="text-sm text-amber-800 font-medium">
              Important Information
            </p>
            <ul className="text-sm text-amber-700 mt-1 space-y-1">
              <li>• Data exports may take several minutes to complete</li>
              <li>• Deactivated accounts can be reactivated within 30 days</li>
              <li>• Account deletion is immediate and cannot be reversed</li>
              <li>
                • Contact support if you need assistance with any of these
                actions
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showDeactivateModal && <DeactivateModal />}
      {showDeleteModal && <DeleteModal />}
    </div>
  );
};

export default AccountActions;
